/* eslint-disable array-callback-return */
import {
  useBoolean,
  useToastr,
} from '@laxmimanogna/code-quick-components';
import { createContext, useEffect, useState } from 'react';
import { successToast } from '../components/successToast';
import { ROLES_VALUES } from '../constants/constants';
import { REBUTTAL_STATUS, RESOLVED_STATUS } from '../pages/audit_sheet/constants';
import auditSheetRepository from '../repositories/AuditSheetRepository';
import { toTitleCase } from '../utils/common.utils';
import { createEmptyTablesProps } from '../utils/auditsheet.utils';

export const AuditSheetContext = createContext({
  isAuditSheetLoading: true,
  createAuditSheet: async () => {},
  createAuditSheetDelta: async () => {},
  getAuditSheet: async () => {},
  monitorAuditHour: async () => {},
  currentUpload: {},
  getAuditUpload: async () => {},
  updateAuditSheet: async () => {},
  getIndustryCodes: async () => {},
  getICDCodes: async () => {},
  getModifiers: async () => {},
  industryCodes: {},
  iCDCodes: {},
  modifiers: {},
  recentAuditImageUpload: async () => {},
  updateActivity: async () => {},
  getUsers: async () => {},
  getAllUsers: async () => {},
  getComments: async () => {},
  comments: [],
  setComments: async () => {},
  users: [],
  allUsers: [],
  isUpdateActivityLoading: false,
  isUserLoading: false,
  isCommentLoading: false,
  allComments: [],
  setAllComments: () => {},
  getAllComments: async () => {},
  commentFilters: {},
  setCommentFilters: () => {},
  getCommentFlag: async () => {},
  commentFlags: {},
  setCommentFlags: () => {},
  selectedCell: {},
  setSelectedCell: () => {},
  selectedRowCol: {},
  setSelectedRowCol: () => {},
  sheetData: {},
  setSheetData: () => {},
  auditsheetReference: {},
  setAuditsheetReference: () => {},
  isAuditFormOpen: {},
  setIsAuditFormOpen: () => {},
  deleteComment: async () => {},
  updateComment: async () => {},
  postComment: async () => {},
  isPostCommentLoading: false,
  isAllCommentLoading: false,
  totalComments: 0,
  providerOptions: [],
  fetchProviderDropdown: async () => {},
  responseAuditIssues: [],
  fetchResponseAuditIssues: async () => {},

  // Booleans
  isProviderDollarDisabled: false,
  ipddState: () => {},
  isAuditedDollarDisabled: false,
  iaddState: () => {},

  // setStates
  setHilightedColumn: () => {},
  setDollorColumn: () => {},
  setAuditSheetColumnsKeys: () => {},
  setAgreeDisAgreeCol: () => {},

  highlightedColumn: () => {},
  dollarColumn: () => {},
  auditSheetColumnsKeys: () => {},
  agreeDisAgreeCol: () => {},

  // batch audit sheet
  getBatchAuditSheet: async () => {},
  isAccessDenied: true,
  getFileObjectByBatchId: async () => {},
  isFileLoading: false,
});
const AuditSheetProvider = props => {
  const toast = useToastr();

  const [isAuditSheetLoading, iasState] = useBoolean(true);
  const [currentUpload, setCurrentUpload] = useState({});
  const [industryCodes, setIndustryCode] = useState({});
  const [iCDCodes, setICDCode] = useState({});
  const [modifiers, setModifiers] = useState({});
  const [users, setUsers] = useState([]);
  const [isUpdateActivityLoading, iuaState] = useBoolean(false);
  const [isCommentLoading, icState] = useBoolean(false);
  const [isUserLoading, iuState] = useBoolean(false);
  const [allUsers, setAllUsers] = useState([]);
  const [isPostCommentLoading, ipclState] = useBoolean(false);
  const [comments, setComments] = useState([]);
  const [allComments, setAllComments] = useState([]);
  const [isAllCommentLoading, iaclState] = useBoolean(false);

  const [commentFlags, setCommentFlags] = useState({});
  const [sheetData, setSheetData] = useState({});

  const [selectedCell, setSelectedCell] = useState({});
  const [isAuditFormOpen, setIsAuditFormOpen] = useBoolean(false);
  const [selectedRowCol, setSelectedRowCol] = useState({});
  const [auditsheetReference, setAuditsheetReference] = useState({});

  const [commentFilters, setCommentFilters] = useState({});
  const [totalComments, setTotalComments] = useState(0);
  const [chartBatchId, setChartBatchId] = useState(null);
  const [providerOptions, setProviderOptions] = useState([]);
  const [responseAuditIssues, setResponseAuditIssues] = useState([]);

  // audit state version to resolve racing issues
  const [auditSheetVersion, setAuditSheetVersion] = useState(0);

  // consts
  const [highlightedColumn, setHilightedColumn] = useState([]);

  const [dollarColumn, setDollorColumn] = useState([]);

  const [auditSheetColumnsKeys, setAuditSheetColumnsKeys] = useState([]);

  const [agreeDisAgreeCol, setAgreeDisAgreeCol] = useState([]);

  // booleans
  const [isProviderDollarDisabled, ipddState] = useBoolean(false);
  const [isAuditedDollarDisabled, iaddState] = useBoolean(false);

  const [isFileLoading, iflState] = useBoolean(false);
  const [isAccessDenied, setAccessDeniedState] = useBoolean(true);

  async function createAuditSheet(payload, uploadId) {
    try {
      const response = await auditSheetRepository.createAuditSheet(
        {version: auditSheetVersion,
        sheets: payload},
        uploadId
      );
      return response;
    } catch (err) {
      toast.showError({
        description: err.toString(),
        duration: 50000
      });
    }
  }
  async function updateAuditSheet(payload, uploadId) {
    try {
      iasState.on();
      const response = await auditSheetRepository.updateAuditSheet(
        {version: auditSheetVersion,
        sheets: payload},
        uploadId
      );
      return response;
    } catch (err) {
      toast.showError({
        description: err.toString(),
        duration: 50000
      });
    } finally {
      iasState.off();
    }
  }

  async function createAuditSheetDelta(payload, uploadId) {
    try {
      const response = await auditSheetRepository.createAuditSheetDelta(
        {
          ...payload,
          version: auditSheetVersion,
        },
        uploadId
      );
      setAuditSheetVersion(response.version);
      return response;
    } catch (err) {
      toast.showError({
        description: err.toString(),
        duration: 5000
      });
      throw err; // Re-throw so calling code can handle
    }
  }

  async function getAuditSheet(uploadId,currentUpload) {
    try {
      iasState.on();
      const response = await auditSheetRepository.getAuditSheet(uploadId);
      setAccessDeniedState.off();
      setAuditSheetVersion(response.version);

      if(!response.sheets.length){
        return createEmptyTablesProps(currentUpload)
      }
      
      return response.sheets;
    } catch (err) {
      const status = err?.status;
      if(status === 404 || status === 403){
        setAccessDeniedState.on();
      }
      else{
        setAccessDeniedState.off();
        toast.showError({
          description: err.toString(),
        });
      }
      return {error: err?.cause?.detail};
    } finally {
      iasState.off();
    }
  }

  async function getBatchAuditSheet(batchId, rendererId) {
    try {
      iasState.on()
      const QUERY_PARAMS = (rendererId) ? {'renderer_id': rendererId} : null;
      const response = await auditSheetRepository.getBatchAuditSheet(batchId, QUERY_PARAMS);
      setAccessDeniedState.off();
      if (!response.length) {
        return [{...createEmptyTablesProps()[0], batch_name: response?.batch_name}];
      }
      return response;
    } catch (err) {
      const status = err?.status;
      if(status === 404 || status === 403){
        setAccessDeniedState.on();
      }
      else{
        setAccessDeniedState.off();
        toast.showError({
          description: err.toString(),
        });
      }
      return {error: err?.cause?.detail};
    } finally {
      iasState.off();
    }
  }

  async function getAuditUpload(uploadId) {
    try {
      iasState.on();
      iflState.on();
      const response = await auditSheetRepository.getAuditUpload(uploadId);
      setAccessDeniedState.off();
      if (Object.keys(response).length) {
        setCurrentUpload(response);
      }
      return response;
    } catch (err) {
      const status = err?.status;
      if(status === 404 || status === 403){
        setAccessDeniedState.on();
      } else {
        setAccessDeniedState.off();
        toast.showError({
          description: err.toString(),
      }); 
    }
    iasState.off();
    return {error: err?.cause?.detail};
    } finally {
      iflState.off();
    }
  }

  const getFileObjectByBatchId = async (batchId, rendererId = null) => {
    try {
      iflState.on();
      let queryParams = null
      if (rendererId) {
        queryParams = {renderer_id: rendererId};
      }
      const response = await auditSheetRepository.getFileObjectByBatchId(
        batchId,
        queryParams
      );
      return response;
    } catch (err) {
      toast.showError({
        description: err.toString(),
        description: `${err.toString()}.`,
      });
    } finally {
      iflState.off();
    }
  }
  
  async function monitorAuditHour(uploadId) {
    try {
      await auditSheetRepository.auditHourMonitor(uploadId);
    } catch (err) {
      toast.showError({
        description: err.toString(),
      });
      return {"error": err.cause.detail};
    }
  }

  async function recentAuditImageUpload(uploadId, data) {
    try {
      await auditSheetRepository.recentAuditImageUpload(uploadId, data);
    } catch (err) {
      toast.showError({
        description: err.toString(),
      });
      return {"error": err.cause.detail};
    }
  }

  async function getIndustryCodes(uploadId) {
    try {
      const response = await auditSheetRepository.getIndustryCodes();
      setIndustryCode(response);
    } catch (err) {
      toast.showError({
        description: err.toString(),
      });
    }
  }

  async function getICDCodes(uploadId) {
    try {
      const response = await auditSheetRepository.getICDCodes();
      setICDCode(response);
    } catch (err) {
      toast.showError({
        description: err.toString(),
      });
    }
  }

  async function getModifiers(uploadId) {
    try {
      const response = await auditSheetRepository.getModifiers();
      setModifiers(response);
    } catch (err) {
      toast.showError({
        description: err.toString(),
      });
    }
  }

  async function updateActivity(data) {
    try {
      iuaState.on();
      const response = await auditSheetRepository.updateActivity(data);
      if (data.length) {
        setCommentFlags(prev => {
          const updated = { ...prev };
          data[0].row.forEach(rowId => {
            updated[rowId] = { ...(prev[rowId] || {}) };
            data[0].column.forEach(col => { updated[rowId][col] = true; });
          });
          return updated;
        });
      }
      const toastProps = {
        header: 'Success!',
        description: `${
          data[0].action === REBUTTAL_STATUS
            ? 'Marked as rebuttal.'
            : !data[0].parent
            ? 'Comment sent'
            : 'Reply sent'
        }`,
      };
      successToast(toast, toastProps);
      return response;
    } catch (err) {
      toast.showError({
        description: err.toString(),
      });
    } finally {
      iuaState.off();
    }
  }

  async function getUsers() {
    try {
      iuState.on();
      const response = await auditSheetRepository.getUsers();
      const userData = response.map(r => {
        return {
          id: r.id,
          display: `${r.first_name} ${r.last_name} | ${ROLES_VALUES[r.role]}`,
          first_name: r.first_name,
          last_name: r.last_name,
        };
      });
      setUsers([...userData]);
    } catch (err) {
      toast.showError({
        description: err.toString(),
      });
    } finally {
      iuState.off();
    }
  }

  async function getAllUsers() {
    try {
      iuState.on();
      const response = await auditSheetRepository.getAllUsers();
      const userData = response.map(r => {
        return {
          id: r.id,
          first_name: r.first_name,
          last_name: r.last_name,
        };
      });
      setAllUsers([...userData]);
    } catch (err) {
      toast.showError({
        description: err.toString(),
      });
    } finally {
      iuState.off();
    }
  }

  async function getComments(uploadId, queryParams) {
    try {
      icState.on();
      const response = await auditSheetRepository.getComments(
        { 
          uploadId, 
          ...queryParams
        }
      );
      // return response
      setComments(response.results ?? []);
    } catch (err) {
      toast.showError({
        description: err.toString(),
      });
      return {"error": err?.cause?.detail};
    } finally {
      icState.off();
    }
  }

  async function getAllComments() {
    try {
      iaclState.on();
      const { uploadId, batchId, ...queryParams } = commentFilters;
      let response;
      if (!uploadId && batchId) {
        response = await auditSheetRepository.getBatchAllComments(batchId, queryParams);
      } else {
        response = await auditSheetRepository.getComments(commentFilters);
      }
      let results = response.results || [];
      let totalCount = response.count || 0;

      // In single chart view, also fetch batch-level notes for the batch this chart belongs to.
      // Derive the batch ID from currentUpload (batch_id field) or fall back to any
      // comment that already carries a batch reference.
      if (commentFilters.uploadId && !commentFilters.batchId) {
        const derivedBatchId =
          currentUpload?.batch_id ||
          currentUpload?.batch ||
          chartBatchId ||
          results.find(c => c.batch)?.batch;
        if (derivedBatchId) {
          const batchResp = await auditSheetRepository.getComments({
            batchId: derivedBatchId,
            reverse: commentFilters.reverse,
            page: 1,
          }).catch(() => ({ results: [] }));
          if (batchResp.results?.length) {
            const existingIds = new Set(results.map(c => c.id));
            const newItems = batchResp.results.filter(c => !existingIds.has(c.id));
            results = [...results, ...newItems];
            // Do NOT add to totalCount — supplementary fetch should not affect pagination.
          }
        }
      }

      if (response.results) {
        setTotalComments(totalCount);
        if (commentFilters.page === 1) {
          setAllComments(results);
        }
        return results;
      }
    } catch (err) {
      toast.showError({
        description: err.toString(),
      });
    } finally {
      iaclState.off();
    }
  }

  async function getRebuttalSubmitValidation(uploadId) {
    try {
      const response = await auditSheetRepository.getRebuttalSubmitValidation(uploadId);
      return response;
    } catch (err) {
      return { can_submit: false, message: 'Validation failed.' };
    }
  }

  async function getCommentFlag({ uploadId, batchId }) {
    try {
      const response = await auditSheetRepository.getCommentFlag({
        uploadId,
        batchId,
      });
      const obj = {};
      if (response.length) {
        for (let i = 0; i < response.length; i++) {
          const comment = response[i];
          const rowId = comment[0];
          const colName = comment[1][0];
          const status = comment[1][1];
          const isOpen = !status || !RESOLVED_STATUS.includes(status);
          if (!obj[rowId]) obj[rowId] = {};
          obj[rowId][colName] = isOpen;
        }
      }
      setCommentFlags(obj);
    } catch (err) {
      toast.showError({
        description: err.toString(),
      });
      return {"error": err.cause.detail};
    }
  }

  async function deleteComment(commentId, commentParentId, notes) {
    try {
      await auditSheetRepository.deleteComment(commentId);
      if (commentParentId) {
        setAllComments(prev => prev.map(c =>
          c.id === commentParentId
            ? { ...c, reply_comments: (c.reply_comments || []).filter(r => r.id !== commentId) }
            : c
        ));
      } else {
        setAllComments(prev => prev.filter(c => c.id !== commentId));
      }
      const toastProps = {
        header: 'Success!',
        description: `${notes ? 'Note' : 'Comment'} is deleted`,
      };
      successToast(toast, toastProps);
    } catch (err) {
      toast.showError({
        description: err.toString(),
      });
    }
  }

  async function updateComment(commentId, payload, commentParentId) {
    try {
      payload.action = 'UPDATE';
      await auditSheetRepository.editComment(commentId, payload);
      if (commentParentId) {
        setAllComments(prev => prev.map(c =>
          c.id === commentParentId
            ? { ...c, reply_comments: c.reply_comments.map(r => r.id === commentId ? { ...r, comment: payload.comment } : r) }
            : c
        ));
      } else {
        setAllComments(prev => prev.map(c => c.id === commentId ? { ...c, comment: payload.comment } : c));
      }
      const toastProps = {
        header: 'Success!',
        description: `${payload.notes ? 'Note' : 'Comment'} is updated`,
      };
      successToast(toast, toastProps);
    } catch (err) {
      toast.showError({
        description: err.toString(),
      });
    }
  }

  async function postComment(data) {
    try {
      ipclState.on();
      if (!data.tagged_user) {
        data.tagged_user = [];
      }
      const response = await auditSheetRepository.postComment(data);
      return response;
    } catch (err) {
      toast.showError({
        description: err.toString(),
      });
    } finally {
      ipclState.off();
    }
  }

  async function fetchProviderDropdown(uploadId) {
    try {
      const response = await auditSheetRepository.getProviderDropdown(uploadId);
      setProviderOptions(
        response.map(p => ({
          id: p.provider_id,
          value: p.full_name
        }))
      );
    } catch (err) {
      toast.showError({
        description: err.toString(),
      });
      return {"error": err.cause.detail};
    }
  }

  async function fetchResponseAuditIssues(uploadId) {
    try {
      const response = await auditSheetRepository.getResponseAuditIssues(uploadId);
      setResponseAuditIssues(response);
    } catch (err) {
      toast.showError({
        description: err.toString(),
      });
    }
  }

  const mContext = {
    isAuditSheetLoading,
    createAuditSheet,
    createAuditSheetDelta,
    getAuditSheet,
    monitorAuditHour,
    currentUpload,
    getAuditUpload,
    updateAuditSheet,
    getIndustryCodes,
    getICDCodes,
    getModifiers,
    industryCodes,
    iCDCodes,
    modifiers,
    recentAuditImageUpload,
    updateActivity,
    getUsers,
    getAllUsers,
    getComments,
    comments,
    setComments,
    users,
    allUsers,
    isUpdateActivityLoading,
    isUserLoading,
    isCommentLoading,
    allComments,
    setAllComments,
    getAllComments,
    commentFilters,
    setCommentFilters,
    chartBatchId,
    setChartBatchId,
    getRebuttalSubmitValidation,
    getCommentFlag,
    commentFlags,
    setCommentFlags,
    selectedCell,
    setSelectedCell,
    selectedRowCol,
    setSelectedRowCol,
    sheetData,
    setSheetData,
    auditsheetReference,
    setAuditsheetReference,
    isAuditFormOpen,
    setIsAuditFormOpen,
    deleteComment,
    updateComment,
    postComment,
    isPostCommentLoading,
    isAllCommentLoading,
    totalComments,
    providerOptions,
    fetchProviderDropdown,
    responseAuditIssues,
    fetchResponseAuditIssues,

    // booleans
    isProviderDollarDisabled,
    ipddState,
    isAuditedDollarDisabled,
    iaddState,

    // setStates
    setHilightedColumn,
    setDollorColumn,
    setAuditSheetColumnsKeys,
    setAgreeDisAgreeCol,

    // states

    highlightedColumn,
    dollarColumn,
    auditSheetColumnsKeys,
    agreeDisAgreeCol,

    getFileObjectByBatchId,
    getBatchAuditSheet,
    isAccessDenied,
    isFileLoading,
  };

  return (
    <AuditSheetContext.Provider value={mContext}>
      {props.children}
    </AuditSheetContext.Provider>
  );
};

export default AuditSheetProvider;